import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { availabilityOverviewQuery, type AvailabilityTotals } from '../api/appAvailability'
import { expiringAppsQuery } from '../api/appExpiry'
import { businessAppsQuery, type BusinessAppSummary } from '../api/businessApps'
import { appVersionsSummaryQuery, type AppVersionIndicator } from '../api/businessAppsVersions'
import { moduleOf, shellQuery } from '../api/shell'
import { AdmPageHead } from '../softsee/adm/AdmPageHead'
import { APPS_ATTENTION_EVENT, AppsAttentionPill } from '../ui/AppsAttentionMark'
import { Forbidden, isForbidden } from '../ui/Forbidden'
import { Content } from '../ui/Shell'
import { Spinner } from '../ui/Spinner'
import { usePageStyles } from '../ui/usePageStyles'
import { AvailabilityCell } from './AppAvailability'
import { BackupBand, BackupMark } from './backup/Backup'
import { MaintenanceBadge } from './AppMaintenance'
import { ExpiringPanel, urgentCount, withExpiry } from './expiry/AppExpiry'
import { AttentionBand, LifecycleBadges } from './Lifecycle'
import { AppFormModal, appUrl, CriticalityPill, mapUrl, STYLES, StatePill } from './parts'
import { vendorsUrl } from './vendors/vendorsApi'
import { QuickCompleteModal } from './QuickComplete'
import { SuggestionsPanel } from './suggestions/SuggestionsPanel'
import { suggestionsQuery } from '../api/businessAppsDetect'
import { SupportCell } from './support/SupportCell'
import { SupportModal } from './support/SupportModal'
import { VersionBadges } from './versions/VersionBadges'

/** Fila de pornire: `?tab=suggested` deschide direct sugestiile. */
function initialTab(): 'list' | 'suggested' | 'expiring' {
  const tab = new URLSearchParams(window.location.search).get('tab')
  return tab === 'suggested' || tab === 'expiring' ? tab : 'list'
}

/** `?attention=1` (insigna rosie din bara): banda se deschide din prima. */
function initialAttention(): number {
  return new URLSearchParams(window.location.search).get('attention') === '1' ? 1 : 0
}

/**
 * Overview > Applications - aplicatiile de business ale clientului curent.
 *
 * "Charisma ERP" nu e un server: e BUCPS-SQL (baza) plus BUCPS-APP (serverul
 * de aplicatie) plus statiile pe care ruleaza clientul. Lista arata aplicatia
 * ca un singur lucru, cu starea ei luata din componente: sanatatea CI-ului
 * (`it_ci.health`) si tichetele / alarmele deschise pe ele.
 *
 * Firma e NUMAI cea din bara (`company_selected`), si cu "All companies"
 * bifat: bifa nu se aplica aplicatiilor (cererea din 6 octombrie 2026, care a
 * inchis si gaura prin care un tehnician vedea aplicatiile clientilor la care
 * n-avea acces). Fara firma aleasa serverul raspunde `needsCompany`, si
 * ecranul cere alegerea ei in loc sa arate tot.
 */
export function ApplicationsPage() {
  const shell = useQuery(shellQuery(moduleOf('/applications')))
  usePageStyles(STYLES, shell.data?.version ?? '')
  const page = useQuery(businessAppsQuery())
  /* Expirarile (contract, licenta, certificate): fila "Expiring" si banda. */
  const expiring = useQuery({ ...expiringAppsQuery(90), retry: false })
  /* Cifra de pe fila Suggested: aceeasi cerere ca panoul, deci una singura. */
  const suggested = useQuery({ ...suggestionsQuery(), retry: false })
  /* Indicatorii de versiune si licente: cer software-ul firmei, deci vin separat, dupa lista. */
  const versions = useQuery({ ...appVersionsSummaryQuery(), retry: false, enabled: page.data?.ready === true })
  /* Procentul pe 30 de zile; fara drept sau inainte de migrare coloana nu apare. */
  const availability = useQuery({ ...availabilityOverviewQuery(), retry: false })
  const [adding, setAdding] = useState(false)
  const [tab, setTab] = useState(initialTab)
  const [attention, setAttention] = useState(initialAttention)
  const [completing, setCompleting] = useState<number | null>(null)
  /* Fereastra de suport, deschisa din celula SUPPORT. */
  const [supporting, setSupporting] = useState<number | null>(null)

  /* Insigna din bara apasata cand lista e deja pe ecran: fila Applications,
     banda deschisa si adusa in vedere. */
  useEffect(() => {
    const open = () => {
      setTab('list')
      setAttention((n) => n + 1)
    }
    window.addEventListener(APPS_ATTENTION_EVENT, open)
    return () => window.removeEventListener(APPS_ATTENTION_EVENT, open)
  }, [])

  if (isForbidden(page.error)) return <Forbidden />
  if (page.error) {
    return (
      <Content>
        <div className="alert alert-danger" role="alert" style={{ margin: 20 }}>
          Something went wrong: {page.error.message}
        </div>
      </Content>
    )
  }
  if (!page.data) return <Spinner />
  const data = { ...page.data, apps: withExpiry(page.data.apps, expiring.data) }
  const soon = urgentCount(expiring.data)
  const needsCompany = data.needsCompany === true
  const showing = data.ready && !needsCompany
  const companyName = data.companies.find((c) => c.id === data.companyId)?.name ?? shell.data?.company?.name ?? ''
  const down = data.apps.filter((a) => a.state === 'down' || a.state === 'alarm').length
  const complete = data.canEdit ? (id: number) => setCompleting(id) : undefined
  const editSupport = data.canEdit ? (id: number) => setSupporting(id) : undefined
  const supportingApp = supporting !== null ? data.apps.find((a) => a.id === supporting) : undefined
  const av = availability.data?.ready === true && Array.isArray(availability.data.apps)
    ? new Map(availability.data.apps.map((a) => [a.appId, a] as const))
    : null

  return (
    <Content>
      {/* Pastila rosie din bara modulului: aplicatiile care asteapta. */}
      <AppsAttentionPill count={shell.data?.appsAttention} />
      <AdmPageHead
        tone="blue-hoki"
        icon="fa-cubes"
        title="Applications"
        note="the business applications, the servers they run on, and who supports them"
        stats={
          showing
            ? [
                { value: String(data.apps.length), label: 'applications', tone: 'blue-hoki' },
                { value: String(down), label: 'affected now', tone: down > 0 ? 'red-intense' : 'green-mode' },
              ]
            : []
        }
        actions={
          showing ? (
            <>
              <a className="btn btn-default" id="appsMapLink" href={mapUrl(data.companyId)} target="_blank" rel="noopener noreferrer">
                <i className="fas fa-chart-pie"></i> Dashboard
              </a>
              <a className="btn btn-default" id="appsVendorsLink" href={vendorsUrl()} target="_blank" rel="noopener noreferrer">
                <i className="fas fa-headset"></i> Vendors
              </a>
              <a
                className="btn btn-default"
                id="appsReportLink"
                href="/app/applications/monthly-report"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fas fa-file-alt"></i> Monthly report
              </a>
              {data.canEdit && (
                <button type="button" className="btn btn-success" id="appAdd" onClick={() => setAdding(true)}>
                  <i className="fas fa-plus"></i> Add application
                </button>
              )}
            </>
          ) : undefined
        }
      />
      {showing && (
        <ul className="nav nav-tabs line-tabs" role="tablist" style={{ margin: '0 20px 12px' }}>
          <li role="presentation" className={tab === 'list' ? 'active' : undefined}>
            <a href="#applications" role="tab" id="appsTabList" onClick={(e) => { e.preventDefault(); setTab('list') }}>
              Applications
              <TabCount n={data.apps.length} />
            </a>
          </li>
          <li role="presentation" className={tab === 'suggested' ? 'active' : undefined}>
            <a href="#suggested" role="tab" id="appsTabSuggested" onClick={(e) => { e.preventDefault(); setTab('suggested') }}>
              <i className="fas fa-magic"></i> Suggested
              <TabCount n={suggested.data?.ready && Array.isArray(suggested.data.suggestions) ? suggested.data.suggestions.length : undefined} />
            </a>
          </li>
          <li role="presentation" className={tab === 'expiring' ? 'active' : undefined}>
            <a href="#expiring" role="tab" id="appsTabExpiring" onClick={(e) => { e.preventDefault(); setTab('expiring') }}>
              <i className="fas fa-hourglass-half"></i> Expiring
              <TabCount n={expiring.data?.ready && Array.isArray(expiring.data.items) ? expiring.data.items.length : undefined} urgent={soon > 0} />
            </a>
          </li>
        </ul>
      )}
      {showing && tab === 'list' && <AttentionBand apps={data.apps} openSignal={attention} onComplete={complete} />}
      {showing && tab === 'list' && <BackupBand />}
      {needsCompany && data.ready ? (
        <div className="alert alert-info" id="appsNeedsCompany" role="status" style={{ margin: 20 }}>
          <i className="fas fa-building"></i> Choose a customer in the company selector at the top: applications are shown
          for one customer at a time.
        </div>
      ) : showing && tab === 'expiring' ? (
        <ExpiringPanel />
      ) : showing && tab === 'suggested' ? (
        <SuggestionsPanel />
      ) : !data.ready ? (
        <div className="alert alert-warning" style={{ margin: 20 }}>
          <i className="fas fa-database"></i> {data.message ?? 'Business applications are not set up on this server yet.'}
        </div>
      ) : (
        <div className="adm-card" style={{ margin: '0 20px 20px' }}>
          {data.apps.length === 0 ? (
            <p className="text-muted" style={{ padding: '10px 15px' }}>
              No business application is recorded for this customer yet. Add one - for example
              the ERP - and the servers it runs on.
            </p>
          ) : (
            <table className="table adm-list" id="appsTable">
              <thead>
                <tr>
                  <th>Application</th>
                  <th>Criticality</th>
                  <th className="text-center">Components</th>
                  <th>Support</th>
                  <th>State</th>
                  {av && <th className="text-right">30-day availability</th>}
                </tr>
              </thead>
              <tbody>
                {data.apps.map((app) => (
                  <AppRow key={app.id} app={app} onComplete={complete} onEditSupport={editSupport} versions={versions.data?.versions} availability={av ? (av.get(app.id) ?? null) : undefined} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      {completing !== null && (
        <QuickCompleteModal appId={completing} onClose={() => setCompleting(null)} />
      )}
      {supportingApp && (
        <SupportModal appId={supportingApp.id} appName={supportingApp.name} onClose={() => setSupporting(null)} />
      )}
      {adding && (
        <AppFormModal
          app={null}
          companyId={data.companyId}
          companyName={companyName}
          criticality={data.criticality}
          onClose={() => setAdding(false)}
          onSaved={(id) => {
            setAdding(false)
            if (id > 0) window.location.assign(appUrl(id))
          }}
        />
      )}
    </Content>
  )
}

function AppRow({
  app,
  onComplete,
  onEditSupport,
  versions,
  availability,
}: {
  app: BusinessAppSummary
  onComplete?: (id: number) => void
  /** Lipsa = fara drept de editare: celula SUPPORT doar se citeste. */
  onEditSupport?: (id: number) => void
  versions?: Record<string, AppVersionIndicator>
  /** undefined = coloana nu apare; null = aplicatia n-are inca nimic socotit. */
  availability?: AvailabilityTotals | null
}) {
  return (
    <tr className={app.state === 'down' || app.state === 'alarm' ? 'adm-row-down' : app.state === 'degraded' ? 'adm-row-degraded' : undefined}>
      <td>
        <a href={appUrl(app.id)} target="_blank" rel="noopener noreferrer">
          <strong>{app.name}</strong>
        </a>
        <LifecycleBadges app={app} />
        <VersionBadges info={versions?.[String(app.id)]} />
        <MaintenanceBadge appId={app.id} />
        {onComplete && app.needsAttention === true ? (
          <button
            type="button"
            className="btn btn-xs btn-default app-complete-btn app-row-complete"
            title="Fill in what is missing, without leaving the list"
            onClick={() => onComplete(app.id)}
          >
            <i className="fas fa-edit"></i> Complete
          </button>
        ) : null}
        {(app.vendor !== '' || app.version !== '') && (
          <div className="adm-muted small">
            {app.vendor}
            {app.vendor !== '' && app.version !== '' ? ' · ' : ''}
            {app.version}
          </div>
        )}
      </td>
      <td>
        <CriticalityPill value={app.criticality} label={app.criticalityLabel} />
      </td>
      <td className="text-center">{app.componentCount}</td>
      <td>
        <SupportCell app={app} onEdit={onEditSupport} />
      </td>
      <td>
        <StatePill state={app.state} label={app.stateLabel} />
        <BackupMark appId={app.id} />
        {app.openTickets > 0 && (
          <span className="adm-muted small" style={{ marginLeft: 6 }}>
            {app.openTickets} open ticket{app.openTickets === 1 ? '' : 's'}
            {app.openAlarms > 0 ? `, ${app.openAlarms} alarm${app.openAlarms === 1 ? '' : 's'}` : ''}
          </span>
        )}
      </td>
      {availability !== undefined && (
        <td className="text-right adm-nowrap">
          <AvailabilityCell totals={availability} />
        </td>
      )}
    </tr>
  )
}

/**
 * Cifra de pe o fila: cate sunt acolo. Rosie cand e ceva urgent (expirari in
 * pragul de alerta), altfel gri. Fara date (fara drept, inainte de migrare)
 * nu apare deloc.
 */
function TabCount({ n, urgent = false }: { n: number | undefined; urgent?: boolean }) {
  if (n === undefined) return null
  return (
    <span className={`badge ${urgent ? 'badge-danger' : 'app-tab-count'}`} style={{ marginLeft: 6 }}>
      {n}
    </span>
  )
}
